import React, { useState } from 'react';
import { useTenantBranding } from '@/context/TenantBranding';
import { apiService } from '@/services/api';

// §auth0-account (FE_PRD_INTAKE_AUTH0_ACCOUNT) + §magic-link (FE_PRD magic
// sign-in): after a successful intake, the BE has created the patient's Auth0
// account (username = email) and returned a one-time password-setup link
// (stashed in localStorage by SquarePaymentForm). This card surfaces:
//  - "Set your password" (when the setup link exists — new patients),
//  - "Email me a magic sign-in link" (additive, passwordless — POST
//    /api/auth/magic-link/start; link lands on dashboard.fitpal.com), and
//  - "Go to my dashboard" → the dashboard login.
// Returning patients / staging / Auth0 errors have no setup link → just the
// magic-link + dashboard options.
const AccountSetupCta = ({ email }) => {
  const { dashboardUrl } = useTenantBranding();
  let url = '';
  try {
    url = (typeof window !== 'undefined' && localStorage.getItem('fitpal_auth0_setup_url')) || '';
  } catch (e) {
    url = '';
  }

  // magic-link state: 'idle' | 'sending' | 'sent' | 'ratelimited' | 'error'
  const [magic, setMagic] = useState('idle');

  const sendMagicLink = async () => {
    if (!email || magic === 'sending') return;
    setMagic('sending');
    try {
      const res = await apiService.startMagicLink(email);
      setMagic(res && res.ok ? 'sent' : 'error');
    } catch (err) {
      setMagic(err?.response?.status === 429 ? 'ratelimited' : 'error');
    }
  };

  return (
    <div
      className="mt-6 mx-auto max-w-md rounded-2xl border border-[#2d8a5f]/20 bg-[#f0faf4] p-5 text-left"
      data-testid="account-setup-cta"
    >
      {url ? (
        <>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Set up your patient portal</h3>
          <p className="text-sm text-gray-600 mb-4">
            Your account is created{email ? <> — your username is <strong>{email}</strong></> : ''}. Set
            your password to access your portal and track your treatment.
          </p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-full rounded-xl bg-[#2d8a5f] px-4 py-3 font-semibold text-white hover:bg-[#246f4d] transition-colors"
            data-testid="set-password-btn"
          >
            Set your password
          </a>
          <p className="text-xs text-gray-400 mt-2">
            We've also emailed you this link. It expires in 7 days — if it lapses, use “Forgot password”
            at sign-in.
          </p>
        </>
      ) : (
        <>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Access your patient portal</h3>
          <p className="text-sm text-gray-600 mb-4">
            Sign in to track your treatment, view messages from your provider, and manage your plan.
          </p>
        </>
      )}

      {/* §magic-link — passwordless alternative (additive) */}
      {email && (
        <div className="mt-4 pt-4 border-t border-[#2d8a5f]/15">
          {magic === 'sent' ? (
            <p className="text-sm text-[#2d8a5f] font-medium" data-testid="magic-link-sent">
              ✓ Check your inbox — we emailed a sign-in link to <strong>{email}</strong>. It expires in
              15 minutes.{' '}
              <button type="button" onClick={() => setMagic('idle')} className="underline font-normal text-[#2d8a5f] hover:text-[#246f4d]" data-testid="magic-link-resend">
                Resend
              </button>
            </p>
          ) : magic === 'ratelimited' ? (
            <p className="text-sm text-amber-700" data-testid="magic-link-ratelimited">
              Too many requests — please try again in a few minutes.
            </p>
          ) : (
            <>
              {url && <p className="text-sm text-gray-600 mb-2">Prefer not to set a password?</p>}
              <button
                type="button"
                onClick={sendMagicLink}
                disabled={magic === 'sending'}
                data-testid="magic-link-btn"
                className={`inline-flex items-center justify-center w-full rounded-xl px-4 py-3 font-semibold transition-colors disabled:opacity-60 ${url ? 'border border-[#2d8a5f] text-[#2d8a5f] hover:bg-[#e8f5ee]' : 'bg-[#2d8a5f] text-white hover:bg-[#246f4d]'}`}
              >
                {magic === 'sending' ? 'Sending…' : 'Email me a magic sign-in link'}
              </button>
              {magic === 'error' && (
                <p className="text-sm text-amber-700 mt-2" data-testid="magic-link-error">
                  We couldn't send the link just now. Please try again, or use your password.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <a
        href={dashboardUrl}
        className="mt-3 inline-flex items-center justify-center w-full rounded-xl border border-gray-300 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        data-testid="dashboard-login-btn"
      >
        Go to my dashboard
      </a>
    </div>
  );
};

export default AccountSetupCta;
